import { motion } from "framer-motion";
import { WORK } from "@/constants/testIds";

const PROJECTS = [
  {
    slug: "urban-mobility-intelligence",
    tag: "Public Sector · Data",
    title: "Urban Mobility Intelligence",
    body: "A real-time forecasting layer for a state transit network — predicting demand and surfacing delays before commuters feel them.",
    image:
      "https://images.unsplash.com/photo-1597673030062-0a0f1a801a31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwbGFwdG9wJTIwc2NyZWVufGVufDB8fHx8MTc4NTkzODgyOXww&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "civic-programme-analytics",
    tag: "Civic Tech · Dashboards",
    title: "Civic Programme Analytics",
    body: "Fund-utilisation and impact dashboards for a national urban development programme, tracking outcomes across hundreds of cities.",
    image:
      "https://images.unsplash.com/photo-1634403665481-74948d815f03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwdGVjaCUyMGRldmljZSUyMHNjcmVlbnxlbnwwfHx8fDE3ODU5Mzg4MTZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "macro-impact-agent",
    tag: "Agentic AI",
    title: "Macro-Impact Agent",
    body: "A conversational agent that explains how macroeconomic shifts move a business's core KPIs — built for finance leaders, not analysts.",
    image:
      "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwc2NyZWVuJTIwZGFyayUyMFVJfGVufDB8fHx8MTc4NTkzOTAxOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "proactive-policing-intelligence",
    tag: "Public Safety · Geospatial AI",
    title: "Proactive Policing Intelligence",
    body: "A geospatial system that maps crime hotspots and optimises patrol vehicle routing in real time, helping a metropolitan police unit respond faster and cover high-risk zones proactively.",
    image:
      "https://images.unsplash.com/photo-1586449480537-3a22cf98b04c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjaXR5JTIwbWFwJTIwZGFzaGJvYXJkJTIwZGFyayUyMGdlb3NwYXRpYWwlMjBoZWF0bWFwJTIwc2NyZWVufGVufDB8fHx8MTc4NTkzOTg2OHww&ixlib=rb-4.1.0&q=85",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function Work() {
  return (
    <section
      id="work"
      data-testid={WORK.section}
      className="relative py-24 md:py-36 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeUp}
        className="mb-14 md:mb-20 max-w-xl"
      >
        <span className="text-xs tracking-[0.25em] uppercase font-medium text-brand">
          Selected Work
        </span>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-ink mt-4">
          A few problems we've shipped through.
        </h2>
        <p className="mt-4 text-base text-muted-foreground max-w-md">
          Client and engagement details are withheld by design — what stays is
          the shape of the problem and how it was solved.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        {PROJECTS.map((p, i) => (
          <motion.div
            key={p.slug}
            data-testid={WORK.card(p.slug)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            transition={{ delay: (i % 2) * 0.12 }}
            className="group relative overflow-hidden rounded-sm border border-border/70 bg-card"
          >
            <div className="relative h-64 md:h-72 overflow-hidden">
              <img
                src={p.image}
                alt={p.title}
                className="w-full h-full object-cover grayscale-[15%] transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
              <span className="absolute top-5 left-5 text-xs tracking-[0.2em] uppercase text-white/90 font-medium">
                {p.tag}
              </span>
            </div>
            <div className="p-7 md:p-8">
              <h3 className="font-display text-xl md:text-2xl tracking-tight text-ink mb-2">
                {p.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
